import React, { useEffect, useState } from "react";
import * as d3 from "d3";
import { useSelector } from 'react-redux';
import { calculatePercentage } from "../../SysGuard/sysGuardConstants";
import "./VennLegend.scss";

const VennLegend = () => {
  const rolesData = useSelector((state) => state.DataguardReducer.rolesData);
  const [sets, setSets] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);

  useEffect(() => {
    if (rolesData && rolesData.combinations) {
      // Calculate total users for percentage calculation
      const totalUsers = rolesData.combinations.reduce((total, combination) => {
        if (combination.sets.length === 1) {
          return total + combination.size;
        }
        return total;
      }, 0);
      
      const modifiedData = rolesData.combinations.map(item => {
        const percentage = calculatePercentage(item.size, totalUsers);
        return {
          ...item,
          tooltip: item.label,
          label: '(' + percentage + '%) ',
          percentage: percentage,
        };
      });
      
      setSets(modifiedData);
      setTotalUsers(totalUsers);
    }
  }, [rolesData]);
  // Extract the same color logic from DataguardRoles
  const getUXColors = () => {
    // Define primary UX colors matching the design
    const primaryColors = [
      '#8B5CF6', // Bright Purple
      '#F97316', // Bright Orange  
      '#06B6D4', // Cyan Blue
      '#EF4444', // Bright Red
      '#10B981', // Emerald Green
      '#F59E0B', // Amber Yellow
      '#EC4899', // Hot Pink
      '#3B82F6', // Blue
      '#84CC16', // Lime Green
      '#8B5A2B', // Brown
      '#6366F1', // Indigo
      '#14B8A6'  // Teal
    ];

    // Get individual roles count
    const individualRoles = sets.filter(set => set.sets.length === 1);
    let uxColors = [...primaryColors];

    // If more than 10 roles, add D3 colors for the additional ones
    if (individualRoles.length > 10) {
      const additionalColorsNeeded = individualRoles.length - 10;

      // Use D3's color schemes for additional colors
      const d3ColorSchemes = [
        d3.schemeCategory10,
        d3.schemeSet1,
        d3.schemeSet2,
        d3.schemeSet3,
        d3.schemePastel1,
        d3.schemePastel2,
        d3.schemeDark2,
        d3.schemeAccent
      ];

      // Flatten all D3 color schemes and remove duplicates
      const allD3Colors = [...new Set(d3ColorSchemes.flat())];

      // Add D3 colors for roles beyond the 10th
      for (let i = 0; i < additionalColorsNeeded && i < allD3Colors.length; i++) {
        uxColors.push(allD3Colors[i]);
      }
    }
    return uxColors;
  };

  // Filter sets to only include individual roles (length = 1)
  const individualRoles = sets.filter(set => set.sets.length === 1);
  const topRoles = individualRoles
    .slice()
    .sort((a, b) => b.size - a.size)
    .slice(0, 3);
  const colors = getUXColors();

  const renderLegendItems = () => {
    return topRoles.map((role, index) => {
      const roleName = role.tooltip || "";
      const color = colors[index];

      return (
        <div key={`legend-${index}`} className="venn-legend-item">
          <div 
            className="venn-legend-circle"
            style={{ backgroundColor: color }}
          />
          <div className="venn-legend-text">
            <span className="venn-legend-name">
              <span className="venn-role-name" title={roleName}>{roleName}</span> 
              <span className="venn-role-percentage" title={`${role.size} users`}>({role.percentage}%)</span>
            </span>
          </div>
        </div>
      );
    });
  };

  // Don't render if no data is available
  if (!rolesData || !rolesData.combinations || sets.length === 0 || individualRoles.length === 0) {
    return null;
  }

  return (
    <div className="venn-legend-container">
      <h3 className="ico-label" title='Top 3 Roles with Most Users'>Top 3 Roles</h3>
      <div className="venn-legend-items">
        {renderLegendItems()}
      </div>
    </div>
  );
};

export default VennLegend;